"use client";
import React from "react";
import { useMediaQuery } from "@mui/material";
import { useTranslations } from "next-intl";
import { LoginForm, LoginFormState } from "../forms/LoginForm";
import { useTheme } from "@quales/utils/theme";
import axios from "axios";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import {
  WcPaper,
  WcStack,
  WcTypography,
  Box,
  WcLink,
  WcButton,
} from "@bpipartners/webui";
import TenantAutoComplete from "./tenant/composites/TenantAutoComplete";
import { TenantAllUserTenant } from "@quales/data-access/gql-server";

export const LoginComposite = ({ loginHost }: { loginHost: string }) => {
  const t = useTranslations();
  const loginRef = React.useRef();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("md"));
  const [failedLogin, setFailedLogin] = React.useState<boolean>(false);

  const [multipleTenants, setMultipleTenants] = React.useState<boolean>(false);
  const [tenantList, setTenantList] = React.useState<TenantAllUserTenant[]>([]);

  const handleLoginDetails = (val: LoginFormState) => {
    if (val) {
      axios
        .post(
          loginHost + "/loginWeb",
          { username: val.userName, password: val.password },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            withCredentials: true,
          }
        )
        .then((res: any) => {
          if (res && res.data && res.data.Status === 200) {
            setFailedLogin(false);
            if (res.data && res.data.multipleTenants) {
              // setTenant("");
              setTenantList(res.data.tenants);
              setMultipleTenants(true);
            } else {
              window.location.href = "/";
            }
          } else {
            setFailedLogin(true);
          }
        })
        .catch((FEerror: any) => {
          setFailedLogin(true);
          console.log("Failed to login, please try again. Error: ", FEerror);
        });
    }
  };

  const handleLoginClicked = () => {
    if (loginRef && loginRef.current) {
      //@ts-ignore
      loginRef.current.onSubmit();
    }
  };

  const handleTenantSelected = (val: any) => {
    if (val) {
      axios
        .post(
          "/auth/switchTenant",
          { newTenantID: val.tenantId },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            withCredentials: true,
          }
        )
        .then((res: any) => {
          if (res && res.data && res.data.Status === 200) {
            window.location.href = "/";
          } else {
            console.log("Failed to switch tenant, please try again. Error: ");
          }
        })
        .catch((FEerror: any) => {
          //@ts-ignore
          if (FEerror.response.status === 404) setExpiredResetLink(true);
        });
    }
  };

  return (
    <WcPaper>
      <WcStack
        alignItems="center"
        height="600px"
        width={isSmallDevice ? "350px" : "600px"}
        id="LoginComposite"
      >
        <WcStack padding={8}>
          <Box
            component="img"
            sx={{
              height: 100,
            }}
            //@ts-ignore
            alt={"Quales-logo"}
            src={"https://storage.googleapis.com/qualesstatic/quales-logo.png"}
          />
        </WcStack>
        {!multipleTenants && (
          <WcStack>
            <WcStack
              mt={2}
              spacing={2}
              justifyContent={"center"}
              alignItems="center"
              width="100%"
            >
              <WcTypography variant="h5">{t("login.loginQuales")}</WcTypography>
              <WcTypography>{t("login.fillDetails")}</WcTypography>
            </WcStack>
            {failedLogin && (
              <WcTypography variant="body1" color={theme.appColors.error}>
                {t("login.invalidCredentials")}
              </WcTypography>
            )}
            <WcStack justifyContent={"center"} alignItems="center" width="100%">
              <LoginForm onLoginDetails={handleLoginDetails} ref={loginRef} />
            </WcStack>

            <WcStack alignItems={"center"} width="100%">
              <WcButton
                color="primary"
                variant="contained"
                onClick={handleLoginClicked}
                sx={{ mr: 1, width: "60%" }}
              >
                {t("login.login")}
              </WcButton>
            </WcStack>
            {isSmallDevice && (
              <>
                <WcStack mt={4} spacing={2} direction={"row"}>
                  <AppleIcon />
                  <WcLink href="https://quales.nl">
                    <WcTypography color={theme.palette.primary.main}>
                      {t("login.appStore")}
                    </WcTypography>
                  </WcLink>
                </WcStack>
                <WcStack spacing={2} direction={"row"} mt={4}>
                  <AndroidIcon />
                  <WcLink href="https://quales.nl">
                    <WcTypography color={theme.palette.primary.main}>
                      {t("login.playStore")}
                    </WcTypography>
                  </WcLink>
                </WcStack>
              </>
            )}
            <WcStack
              paddingTop={10}
              justifyContent={"center"}
              alignItems="center"
              width="100%"
            >
              <WcLink href="/forgotPassword">
                <WcTypography color={theme.palette.primary.main}>
                  {t("login.forgotPassword")}
                </WcTypography>
              </WcLink>
            </WcStack>
          </WcStack>
        )}

        {multipleTenants && tenantList && tenantList.length > 0 && (
          <WcStack alignItems={"center"} padding={4}>
            <WcStack
              m={4}
              spacing={8}
              justifyContent={"center"}
              alignItems="center"
              width="100%"
            >
              <WcTypography variant="body2">
                {t("login.selectTenant")}
              </WcTypography>
            </WcStack>

            <WcStack
              justifyContent={"center"}
              alignItems="center"
              width="100%"
              mt={8}
            >
              <TenantAutoComplete
                edit
                width={300}
                InputPropsValue={""}
                tenants={tenantList}
                onValueChanged={handleTenantSelected}
              />
            </WcStack>
          </WcStack>
        )}
      </WcStack>
    </WcPaper>
  );
};

export default LoginComposite;
